/**
 * Chande Momentum Oscillator (CMO)
 *
 * Measures momentum as the normalized difference between up and down sums.
 * CMO = 100 × (sumUp − sumDown) / (sumUp + sumDown)
 * Ranges from −100 to +100.
 *
 * Category: Momentum | Pane: Separate
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { getSource } from "./sma";

export const cmoIndicator: IndicatorDefinition = {
  id: "cmo",
  name: "Chande Momentum Oscillator",
  shortName: "CMO",
  category: "momentum",
  pane: "separate",
  params: [
    { key: "period", label: "Period", type: "number", default: 9, min: 2, max: 200 },
    { key: "source", label: "Source", type: "select", default: "close", options: [
      { label: "Close", value: "close" }, { label: "Open", value: "open" },
      { label: "High", value: "high" }, { label: "Low", value: "low" },
    ]},
    { key: "color", label: "Color", type: "color", default: "#009688" },
  ],
  plots: [
    { key: "cmo", label: "CMO", color: "#009688", style: "line", lineWidth: 2, priceScaleId: "cmo" },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { period, source = "close" } = params;
    const src = getSource(candles, source);
    const data: { time: number; value: number }[] = [];

    for (let i = period; i < src.length; i++) {
      let sumUp = 0, sumDown = 0;
      for (let j = i - period + 1; j <= i; j++) {
        const diff = src[j] - src[j - 1];
        if (diff > 0) sumUp += diff;
        else sumDown += Math.abs(diff);
      }
      const total = sumUp + sumDown;
      const value = total === 0 ? 0 : 100 * (sumUp - sumDown) / total;
      data.push({ time: candles[i].time, value });
    }

    return [{ key: "cmo", data }];
  },
};
